import type { SelfReportedRow, SelfReportedSignups, SignupsPoint, SignupsReport } from '@/api/signups'
import type { PeriodInfo } from '@/api/types'
import { minorUnitExponent } from '@/design/format'
import { addDays, type DateWindow, type GlobalFilters } from '@/lib/filters'

import { wobble } from '../random'
import { DEMO_TIMEZONE, type DayTotals, type DemoDataset } from './engine'
import { currentAndPrevious, periodInfo } from './overview'
import { signupsIn, signupsOn } from './signups'

/** Where the demo's measured, attributed sign-ups came from, and their shares. */
const SIGNUP_CHANNELS = [
  { channel: 'paid_social', label: 'Paid social', share: 0.42 },
  { channel: 'organic_search', label: 'Organic search', share: 0.24 },
  { channel: 'email', label: 'Email', share: 0.18 },
  { channel: 'direct', label: 'Direct', share: 0.16 },
] as const

/**
 * The answers a demo tenant's customers give to "Where did you hear about us?" when its
 * fixture names none, in the order each first appeared (the API's fixed row order), with
 * roughly their shares. `null` is the sign-ups who gave no answer: always last, never a row.
 */
const DEFAULT_SELF_REPORTED_ANSWERS: ReadonlyArray<{ answer: string | null; weight: number }> = [
  { answer: 'Facebook ad', weight: 0.32 },
  { answer: 'Instagram', weight: 0.2 },
  { answer: 'A friend', weight: 0.12 },
  { answer: 'TikTok', weight: 0.06 },
  { answer: 'Google', weight: 0.05 },
  { answer: 'Other', weight: 0.08 },
  { answer: null, weight: 0.17 },
]

/** Members before the demo's first day of history: the account base the sign-ups add to. */
const DEMO_MEMBER_BASE = 2400
/** How far back the demo counts sign-ups into the member base. */
const MEMBER_HISTORY_DAYS = 120

/** One day's sign-ups split across the answers, wobbled per answer and day, by largest remainder so they sum to the day. */
function splitDay(answers: typeof DEFAULT_SELF_REPORTED_ANSWERS, seed: number, date: string, signups: number): number[] {
  const weights = answers.map(({ answer, weight }) => weight * wobble(seed, 0.6, 'self_reported', answer ?? '', date))
  const sum = weights.reduce((total, weight) => total + weight, 0)
  const exact = weights.map((weight) => (signups * weight) / sum)
  const counts = exact.map(Math.floor)
  let left = signups - counts.reduce((total, count) => total + count, 0)
  const order = exact.map((value, index) => ({ index, rest: value - Math.floor(value) })).sort((a, b) => b.rest - a.rest || a.index - b.index)
  for (const { index } of order) {
    if (left <= 0) break
    counts[index] = (counts[index] ?? 0) + 1
    left -= 1
  }
  return counts
}

function share(count: number, total: number): number | null {
  return total === 0 ? null : Math.round((count / total) * 10_000) / 10_000
}

/**
 * The demo's `by_self_reported`: each day's sign-ups split across the tenant's answers, so
 * the rows plus "No answer" always sum to the total, day by day and for both periods.
 */
interface DemoSelfReportedInput {
  dataset: DemoDataset
  currentDays: readonly DayTotals[]
  previousDays: readonly DayTotals[]
  points: readonly SignupsPoint[]
  signups: number
  priorSignups: number
  /** The days before the range whose sign-ups are members at its start. */
  memberDays: readonly DayTotals[]
}

function demoSelfReported({
  dataset,
  currentDays,
  previousDays,
  points,
  signups,
  priorSignups,
  memberDays,
}: DemoSelfReportedInput): SelfReportedSignups {
  const { seed, currency } = dataset.tenant
  const answers = dataset.tenant.selfReportedAnswers ?? DEFAULT_SELF_REPORTED_ANSWERS
  const currentSplit = points.map((point) => splitDay(answers, seed, point.date, point.signups))
  const previousSplit = previousDays.map((day) => splitDay(answers, seed, day.date, signupsOn(seed, day)))
  const all: SelfReportedRow[] = answers.map(({ answer }, index) => {
    const dayPoints = points.map((point, day) => ({ date: point.date, signups: currentSplit[day]?.[index] ?? 0 }))
    const count = dayPoints.reduce((total, point) => total + point.signups, 0)
    const previous = previousSplit.reduce((total, day) => total + (day[index] ?? 0), 0)
    return { answer, signups: count, share: share(count, signups), previous, change: count - previous, points: dayPoints }
  })
  const noAnswer: SelfReportedRow = all.find((row) => row.answer === null) ?? {
    answer: null,
    signups: 0,
    share: share(0, signups),
    previous: 0,
    change: 0,
    points: points.map((point) => ({ date: point.date, signups: 0 })),
  }
  const rows = all.filter((row) => row.answer !== null && (row.signups > 0 || row.previous > 0))

  const membersAtStart = DEMO_MEMBER_BASE + signupsIn(seed, memberDays)
  const facebook = rows.find((row) => row.answer === 'Facebook ad')?.signups ?? 0
  const metaSpend = currentDays.reduce((total, day) => total + (day.channels.get('meta_ads')?.spend ?? 0), 0)
  const spendMinor = Math.round(metaSpend * 10 ** minorUnitExponent(currency))
  const spendUnavailable = spendMinor === 0 ? 'no_spend' : facebook === 0 ? 'no_facebook_ad_signups' : null
  return {
    rows,
    noAnswer,
    answeredShare: share(signups - noAnswer.signups, signups),
    membersAtStart,
    membersAtEnd: membersAtStart + signups,
    memberGrowthPct: membersAtStart === 0 ? null : Math.round((signups / membersAtStart) * 1000) / 10,
    vsPreviousRatio: priorSignups === 0 ? null : Math.round((signups / priorSignups) * 10) / 10,
    spendPerFacebookAdSignup:
      spendUnavailable === null ? { spendMinor, perSignupMinor: Math.round(spendMinor / facebook), currency } : null,
    spendUnavailable,
  }
}

/**
 * The demo's `GET /analytics/signups`: the Overview tile's daily sign-ups
 * (`mock/generators/signups`) against the tile's own comparison period, so the panel and
 * the tile agree, and a channel split that always sums to the total. With comparison off
 * the previous period is the same number of days just before, as the API's always is.
 */
export function demoSignups(dataset: DemoDataset, filters: GlobalFilters): { data: SignupsReport; period: PeriodInfo } {
  const { seed } = dataset.tenant
  const { period, scope, current, previous } = currentAndPrevious(dataset, filters)
  const range = period.current
  const before: DateWindow = { from: addDays(range.from, -range.days), to: addDays(range.from, -1), days: range.days }
  const previousDays = previous?.days ?? dataset.period(before, scope).days
  const points = current.days.map((day) => ({ date: day.date, signups: signupsOn(seed, day) }))
  const previousPoints = previousDays.map((day) => ({ date: day.date, signups: signupsOn(seed, day) }))
  const signups = signupsIn(seed, current.days)
  const priorSignups = signupsIn(seed, previousDays)

  // Measured: the share whose visitor allowed analytics; of those, a few not yet attributed.
  const measured = Math.round(signups * 0.62)
  const unattributed = Math.min(measured, Math.round(signups * 0.05))
  const attributed = measured - unattributed
  let placed = 0
  const byChannel = SIGNUP_CHANNELS.map(({ channel, label, share }, index) => {
    const count = index === SIGNUP_CHANNELS.length - 1 ? attributed - placed : Math.round(attributed * share)
    placed += count
    return { channel, label, signups: count }
  })
    .filter((entry) => entry.signups > 0)
    .sort((a, b) => b.signups - a.signups)
  const converted = Math.round(signups * 0.37)
  const history: DateWindow = { from: addDays(range.from, -MEMBER_HISTORY_DAYS), to: addDays(range.from, -1), days: MEMBER_HISTORY_DAYS }
  const bySelfReported = demoSelfReported({
    dataset,
    currentDays: current.days,
    previousDays,
    points,
    signups,
    priorSignups,
    memberDays: dataset.period(history, scope).days,
  })

  return {
    period: periodInfo(period),
    data: {
      totals: {
        signups,
        previous: priorSignups,
        change: priorSignups === 0 ? null : Math.round(((signups - priorSignups) / priorSignups) * 10_000) / 10_000,
      },
      points,
      previousPoints,
      converted: { count: converted, rate: signups === 0 ? null : Math.round((converted / signups) * 10_000) / 10_000 },
      byChannel,
      unattributed,
      unmeasured: signups - measured,
      measured: {
        registrationStarted: Math.round(measured * 2.6),
        registrationCompleted: measured,
        pendingSync: 0,
      },
      coverage: {
        firstEventAt: `${range.from}T00:00:00Z`,
        measuredFrom: `${range.from}T00:00:00Z`,
        note: 'Sign-ups are counted from the website’s synced customers, so every account counts; the channel split and the registration figures come from visitors who allowed analytics. Demo data.',
      },
      bySelfReported,
      timezone: DEMO_TIMEZONE,
    },
  }
}
